import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import SessionTimer from '../components/SessionTimer';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [cars, setCars] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fonction helper pour nettoyer les espaces des nombres
  const cleanNumber = (value) => {
    if (!value && value !== 0) return '';
    return value.toString().replace(/\s/g, '');
  };

  const [newCar, setNewCar] = useState({
    vo: '',
    dateImmatriculation: '',
    marque: '',
    modele: '',
    annee: '',
    kilometrage: '',
    prix: '',
    energie: 'Essence',
    transmission: 'Manuelle',
    couleur: '',
    puissanceFiscale: '',
    co2: '',
    critAir: '',
    images: [],
    description: '',
    disponible: true,
    enVedette: false
  });

  useEffect(() => {
    // Vérifier l'authentification
    if (!apiService.isAuthenticated()) {
      navigate('/admin');
      return;
    }

    // Charger les données
    loadCarsData();
  }, [navigate]);

  const loadCarsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [carsResponse, statsResponse] = await Promise.all([
        apiService.getCars(),
        apiService.getCarStats()
      ]);
      
      if (carsResponse.success) {
        setCars(carsResponse.data);
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await loadCarsData();
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      navigate('/admin');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const targetState = editingCar ? setEditingCar : setNewCar;
    
    targetState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddCar = async (e) => {
    e.preventDefault();
    
    try {
      // Traitement des données du formulaire
      const formData = new FormData();
      
      // Préfixer VO coté client si fourni en chiffres
      const voDigits = (newCar.vo || '').toString().replace(/\D/g, '');
      const voToSend = voDigits ? `VO${voDigits}` : '';

      // Ajouter tous les champs sauf les images
      Object.keys(newCar).forEach(key => {
        if (key !== 'images') {
          if (key === 'vo') {
            formData.append('vo', voToSend);
          } else if (['annee', 'kilometrage', 'prix', 'puissanceFiscale', 'co2', 'critAir'].includes(key)) {
            // Nettoyer les espaces avant de convertir en nombre
            formData.append(key, parseInt(cleanNumber(newCar[key])));
          } else if (key === 'disponible' || key === 'enVedette') {
            // Convertir explicitement les booléens en strings
            formData.append(key, newCar[key] ? 'true' : 'false');
          } else {
            formData.append(key, newCar[key]);
          }
        }
      });
      
      // Ajouter les fichiers images
      if (newCar.images && newCar.images.length > 0) {
        newCar.images.forEach((file, index) => {
          formData.append(`images`, file);
        });
      }
      
      const carToAdd = formData;

      const response = await apiService.createCar(carToAdd);
      
      if (response.success) {
        // Réinitialiser le formulaire
        setNewCar({
          vo: '',
          dateImmatriculation: '',
          marque: '',
          modele: '',
          annee: '',
          kilometrage: '',
          prix: '',
          energie: 'Essence',
          transmission: 'Manuelle',
          couleur: '',
          puissanceFiscale: '',
          co2: '',
          critAir: '',
          images: [],
          description: '',
          disponible: true,
          enVedette: false
        });
        
        // Réinitialiser l'input file
        const fileInput = document.querySelector('input[type="file"][name="images"]');
        if (fileInput) {
          fileInput.value = '';
        }
        setShowAddForm(false);
        
        // Recharger les données
        await refreshData();
        
        alert('Véhicule ajouté avec succès !');
      } else {
        alert('Erreur lors de l\'ajout du véhicule');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      if (error.message && error.message.includes('Limite atteinte')) {
        alert('⚠️ Limite atteinte : maximum 3 véhicules en vedette.\n\nRetirez un véhicule de la vedette avant d\'en ajouter un nouveau.');
      } else {
        alert('Erreur lors de l\'ajout du véhicule: ' + error.message);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleEditCar = (car) => {
    setEditingCar({ ...car });
    scrollToTop();
  };

  const handleUpdateCar = async (e) => {
    e.preventDefault();
    scrollToTop();
    
    try {
      // Traitement des données du formulaire d'édition
      const formData = new FormData();

      // Préfixer VO coté client si fourni en chiffres
      const voDigits = (editingCar.vo || '').toString().replace(/\D/g, '');
      const voToSend = voDigits ? `VO${voDigits}` : '';
      
      // Ajouter tous les champs sauf les images
      Object.keys(editingCar).forEach(key => {
        if (key !== 'images') {
          if (key === 'vo') {
            formData.append('vo', voToSend);
          } else if (['annee', 'kilometrage', 'prix', 'puissanceFiscale', 'co2', 'critAir'].includes(key)) {
            // Nettoyer les espaces avant de convertir en nombre
            formData.append(key, parseInt(cleanNumber(editingCar[key])));
          } else if (key === 'disponible' || key === 'enVedette') {
            // Convertir explicitement les booléens en strings
            formData.append(key, editingCar[key] ? 'true' : 'false');
          } else {
            formData.append(key, editingCar[key]);
          }
        }
      });
      
      // Ajouter les fichiers images s'il y en a de nouveaux
      if (editingCar.images && editingCar.images.length > 0 && editingCar.images[0] instanceof File) {
        editingCar.images.forEach((file) => {
          formData.append(`images`, file);
        });
      } else if (editingCar.images && editingCar.images.length > 0) {
        // Conserver les images existantes (URLs)
        formData.append('existingImages', JSON.stringify(editingCar.images));
      }
      
      const carToUpdate = formData;

      const response = await apiService.updateCar(editingCar.id, carToUpdate);
      
      if (response.success) {
        setEditingCar(null);
        await refreshData();
        alert('Véhicule modifié avec succès !');
      } else {
        alert('Erreur lors de la modification du véhicule');
      }
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      if (error.message && error.message.includes('Limite atteinte')) {
        alert('⚠️ Limite atteinte : maximum 3 véhicules en vedette.\n\nRetirez un véhicule de la vedette avant d\'en ajouter un nouveau.');
      } else {
        alert('Erreur lors de la modification du véhicule: ' + error.message);
      }
    }
  };

  const handleDeleteCar = async (carId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) {
      try {
        const response = await apiService.deleteCar(carId);
        
        if (response.success) {
          await refreshData();
          alert('Véhicule supprimé avec succès !');
        } else {
          alert('Erreur lors de la suppression du véhicule');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du véhicule: ' + error.message);
      }
    }
  };

  const toggleAvailability = async (carId) => {
    try {
      const car = cars.find(c => c.id === carId);
      if (!car) return;
      
      const updatedCar = { ...car, disponible: !car.disponible };
      const response = await apiService.updateCar(carId, updatedCar);
      
      if (response.success) {
        await refreshData();
      } else {
        alert('Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du statut: ' + error.message);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Affichage du loader ou des erreurs
  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="container">
          <div className="loading-message">
            <h2>Chargement des données...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="container">
          <div className="error-message">
            <h2>Erreur</h2>
            <p>{error}</p>
            <button onClick={refreshData} className="btn btn-primary">
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="container">
        {/* Header */}
        <div className="dashboard-header">
          <h1>📊 Tableau de bord administrateur</h1>
          <div className="header-actions">
            <SessionTimer />
            <button 
              onClick={() => navigate('/')} 
              className="btn btn-outline"
            >
              Voir le site
            </button>
            <button onClick={handleLogout} className="btn btn-secondary">
              Déconnexion
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total véhicules</h3>
            <div className="stat-number">{stats.total}</div>
          </div>
          <div className="stat-card">
            <h3>Disponibles</h3>
            <div className="stat-number available">{stats.available}</div>
          </div>
          <div className="stat-card">
            <h3>Vendus</h3>
            <div className="stat-number sold">{stats.sold}</div>
          </div>
          <div className="stat-card">
            <h3>⭐ En vedette</h3>
            <div className="stat-number" style={{color: 'var(--primary-gold)'}}>{stats.featured || 0}/3</div>
          </div>
          <div className="stat-card">
            <h3>Valeur stock</h3>
            <div className="stat-number">{formatPrice(stats.totalValue || 0)}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="dashboard-actions">
          <button 
              onClick={() => {
                setShowAddForm(!showAddForm);
                if (!showAddForm) scrollToTop();
              }}
              className="btn btn-primary"
            >
              {showAddForm ? 'Annuler' : '+ Ajouter un véhicule'}
            </button>
        </div>

        {/* Formulaire d'ajout */}
        {showAddForm && (
          <div className="car-form">
            <h3>Ajouter un nouveau véhicule</h3>
            <form onSubmit={handleAddCar}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Numéro VO:</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="vo"
                    value={newCar.vo}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setNewCar(prev => ({ ...prev, vo: digits }));
                    }}
                    placeholder="Ex: 12345"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date d'immatriculation:</label>
                  <input
                    type="date"
                    name="dateImmatriculation"
                    value={newCar.dateImmatriculation}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Marque:</label>
                  <input
                    type="text"
                    name="marque"
                    value={newCar.marque}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Modèle:</label>
                  <input
                    type="text"
                    name="modele"
                    value={newCar.modele}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Année:</label>
                  <input
                    type="number"
                    name="annee"
                    value={newCar.annee}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Kilométrage:</label>
                  <input
                    type="text"
                    name="kilometrage"
                    value={newCar.kilometrage}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Prix (€):</label>
                  <input
                    type="text"
                    name="prix"
                    value={newCar.prix}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Énergie:</label>
                  <select
                    name="energie"
                    value={newCar.energie}
                    onChange={handleInputChange}
                  >
                    <option value="Essence">Essence</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Hybride">Hybride</option>
                    <option value="Hybride rechargeable">Hybride rechargeable</option>
                    <option value="Électrique">Électrique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Transmission:</label>
                  <select
                    name="transmission"
                    value={newCar.transmission}
                    onChange={handleInputChange}
                  >
                    <option value="Manuelle">Manuelle</option>
                    <option value="Automatique">Automatique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Couleur:</label>
                  <input
                    type="text"
                    name="couleur"
                    value={newCar.couleur}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Puissance fiscale (CV):</label>
                  <input
                    type="number"
                    name="puissanceFiscale"
                    value={newCar.puissanceFiscale}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Émissions CO2 (g/km):</label>
                  <input
                    type="number"
                    name="co2"
                    value={newCar.co2}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Crit'Air:</label>
                  <select
                    name="critAir"
                    value={newCar.critAir}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    <option value="0">Crit'Air 0 (Électrique/Hydrogène)</option>
                    <option value="1">Crit'Air 1</option>
                    <option value="2">Crit'Air 2</option>
                    <option value="3">Crit'Air 3</option>
                    <option value="4">Crit'Air 4</option>
                    <option value="5">Crit'Air 5</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Images du véhicule (1 à 5 images) :</label>
                <div className="image-upload-zone" style={{
                  border: '2px dashed rgba(255, 215, 0, 0.5)',
                  borderRadius: '12px',
                  padding: '30px 20px',
                  textAlign: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}>
                  <input
                    type="file"
                    name="images"
                    multiple
                    accept=".jpg,.jpeg,.png,.heic,.heif,.webp"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 5) {
                        alert('Vous ne pouvez sélectionner que 5 images maximum.');
                        e.target.value = '';
                        return;
                      }
                      setNewCar(prev => ({ ...prev, images: files }));
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ pointerEvents: 'none' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>📸</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--primary-gold)', marginBottom: '8px' }}>
                      Cliquez ici pour sélectionner vos images
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '12px' }}>
                      ou glissez-déposez vos fichiers dans cette zone
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      <strong>📋 Comment sélectionner plusieurs images :</strong><br/>
                      • <strong>Sur PC :</strong> Maintenez <kbd style={{background: 'rgba(255,215,0,0.2)', padding: '2px 6px', borderRadius: '4px'}}>Ctrl</kbd> et cliquez sur chaque image<br/>
                      • <strong>Sur Mac :</strong> Maintenez <kbd style={{background: 'rgba(255,215,0,0.2)', padding: '2px 6px', borderRadius: '4px'}}>Cmd</kbd> et cliquez sur chaque image<br/>
                      • <strong>Formats :</strong> JPG, PNG, HEIC, WebP (max 5 images)
                    </div>
                  </div>
                </div>
                
                {newCar.images && newCar.images.length > 0 && (
                  <div style={{
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '10px',
                      color: 'var(--success-color)',
                      fontWeight: '600'
                    }}>
                      <span>✅</span>
                      <strong>{newCar.images.length} image{newCar.images.length > 1 ? 's' : ''} sélectionnée{newCar.images.length > 1 ? 's' : ''}</strong>
                      {newCar.images.length < 5 && (
                        <span style={{ fontSize: '0.9rem', color: '#9ca3af' }}>
                          (Vous pouvez en ajouter {5 - newCar.images.length} de plus)
                        </span>
                      )}
                    </div>
                    <ul style={{margin: '0', paddingLeft: '20px', fontSize: '0.9rem', color: '#e5e7eb'}}>
                      {newCar.images.map((file, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>
                          <strong>{file.name}</strong> 
                          <span style={{ color: '#9ca3af', marginLeft: '8px' }}>
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  name="description"
                  value={newCar.description}
                  onChange={handleInputChange}
                  rows="3"
                  required
                />
              </div>
              <div className="form-group">
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="enVedette"
                    checked={newCar.enVedette}
                    onChange={handleInputChange}
                    style={{width: 'auto', cursor: 'pointer'}}
                  />
                  <div>
                    <strong style={{color: 'var(--primary-gold)'}}>⭐ Mettre ce véhicule en vedette</strong>
                    <div style={{fontSize: '0.85rem', color: '#9ca3af', marginTop: '4px'}}>
                      Maximum 3 véhicules en vedette. Actuellement : {stats.featured || 0}/3
                    </div>
                  </div>
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Ajouter le véhicule
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowAddForm(false)}
                  className="btn btn-outline"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Formulaire d'édition */}
        {editingCar && (
          <div className="car-form">
            <h3>Modifier le véhicule</h3>
            <form onSubmit={handleUpdateCar}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Numéro VO:</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="vo"
                    value={editingCar.vo ? editingCar.vo.replace(/^VO/, '') : ''}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setEditingCar(prev => ({ ...prev, vo: digits }));
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date d'immatriculation:</label>
                  <input
                    type="date"
                    name="dateImmatriculation"
                    value={editingCar.dateImmatriculation || ''}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Marque:</label>
                  <input
                    type="text"
                    name="marque"
                    value={editingCar.marque}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Modèle:</label>
                  <input
                    type="text"
                    name="modele"
                    value={editingCar.modele}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Année:</label>
                  <input
                    type="number"
                    name="annee"
                    value={editingCar.annee}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Kilométrage:</label>
                  <input
                    type="number"
                    name="kilometrage"
                    value={editingCar.kilometrage}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Prix (€):</label>
                  <input
                    type="text"
                    name="prix"
                    value={editingCar.prix}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Énergie:</label>
                  <select
                    name="energie"
                    value={editingCar.energie}
                    onChange={handleInputChange}
                  >
                    <option value="Essence">Essence</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Hybride">Hybride</option>
                    <option value="Hybride rechargeable">Hybride rechargeable</option>
                    <option value="Électrique">Électrique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Transmission:</label>
                  <select
                    name="transmission"
                    value={editingCar.transmission}
                    onChange={handleInputChange}
                  >
                    <option value="Manuelle">Manuelle</option>
                    <option value="Automatique">Automatique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Couleur:</label>
                  <input
                    type="text"
                    name="couleur"
                    value={editingCar.couleur}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Puissance fiscale (CV):</label>
                  <input
                    type="number"
                    name="puissanceFiscale"
                    value={editingCar.puissanceFiscale}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Émissions CO2 (g/km):</label>
                  <input
                    type="number"
                    name="co2"
                    value={editingCar.co2}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Crit'Air:</label>
                  <select
                    name="critAir"
                    value={editingCar.critAir}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    <option value="0">Crit'Air 0 (Électrique/Hydrogène)</option>
                    <option value="1">Crit'Air 1</option>
                    <option value="2">Crit'Air 2</option>
                    <option value="3">Crit'Air 3</option>
                    <option value="4">Crit'Air 4</option>
                    <option value="5">Crit'Air 5</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Images du véhicule (1 à 5 images) :</label>
                <div className="image-upload-zone" style={{
                  border: '2px dashed rgba(255, 215, 0, 0.5)',
                  borderRadius: '12px',
                  padding: '30px 20px',
                  textAlign: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}>
                  <input
                    type="file"
                    name="images"
                    multiple
                    accept=".jpg,.jpeg,.png,.heic,.heif,.webp"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 5) {
                        alert('Vous ne pouvez sélectionner que 5 images maximum.');
                        e.target.value = '';
                        return;
                      }
                      setEditingCar(prev => ({ ...prev, images: files }));
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ pointerEvents: 'none' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>📸</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--primary-gold)', marginBottom: '8px' }}>
                      Cliquez ici pour remplacer les images
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '12px' }}>
                      ou glissez-déposez de nouvelles images dans cette zone
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      <strong>📋 Comment sélectionner plusieurs images :</strong><br/>
                      • <strong>Sur PC :</strong> Maintenez <kbd style={{background: 'rgba(255,215,0,0.2)', padding: '2px 6px', borderRadius: '4px'}}>Ctrl</kbd> et cliquez sur chaque image<br/>
                      • <strong>Sur Mac :</strong> Maintenez <kbd style={{background: 'rgba(255,215,0,0.2)', padding: '2px 6px', borderRadius: '4px'}}>Cmd</kbd> et cliquez sur chaque image<br/>
                      • <strong>Formats :</strong> JPG, PNG, HEIC, WebP (max 5 images)
                    </div>
                  </div>
                </div>
                
                {editingCar.images && editingCar.images.length > 0 && (
                  <div style={{
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: editingCar.images[0] instanceof File 
                      ? 'rgba(34, 197, 94, 0.1)' 
                      : 'rgba(59, 130, 246, 0.1)',
                    border: editingCar.images[0] instanceof File 
                      ? '1px solid rgba(34, 197, 94, 0.3)' 
                      : '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '10px',
                      color: editingCar.images[0] instanceof File ? 'var(--success-color)' : '#3b82f6',
                      fontWeight: '600'
                    }}>
                      <span>{editingCar.images[0] instanceof File ? '✅' : '🖼️'}</span>
                      <strong>
                        {editingCar.images[0] instanceof File 
                          ? `${editingCar.images.length} nouvelle${editingCar.images.length > 1 ? 's' : ''} image${editingCar.images.length > 1 ? 's' : ''} sélectionnée${editingCar.images.length > 1 ? 's' : ''}`
                          : `${Array.isArray(editingCar.images) ? editingCar.images.length : 0} image${Array.isArray(editingCar.images) && editingCar.images.length > 1 ? 's' : ''} actuelle${Array.isArray(editingCar.images) && editingCar.images.length > 1 ? 's' : ''}`
                        }
                      </strong>
                      {editingCar.images[0] instanceof File && editingCar.images.length < 5 && (
                        <span style={{ fontSize: '0.9rem', color: '#9ca3af' }}>
                          (Vous pouvez en ajouter {5 - editingCar.images.length} de plus)
                        </span>
                      )}
                    </div>
                    <ul style={{margin: '0', paddingLeft: '20px', fontSize: '0.9rem', color: '#e5e7eb'}}>
                      {Array.isArray(editingCar.images) && editingCar.images.map((item, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>
                          <strong>
                            {item instanceof File ? item.name : (typeof item === 'string' ? item.split('/').pop() : item)}
                          </strong>
                          {item instanceof File && (
                            <span style={{ color: '#9ca3af', marginLeft: '8px' }}>
                              ({(item.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {editingCar.images[0] instanceof File && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '0.85rem', 
                        color: '#fbbf24',
                        fontStyle: 'italic'
                      }}>
                        ⚠️ Ces nouvelles images remplaceront les images actuelles lors de la sauvegarde.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  name="description"
                  value={editingCar.description}
                  onChange={handleInputChange}
                  rows="3"
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="disponible"
                    checked={editingCar.disponible}
                    onChange={handleInputChange}
                  />
                  Véhicule disponible
                </label>
              </div>
              <div className="form-group">
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="enVedette"
                    checked={editingCar.enVedette || false}
                    onChange={handleInputChange}
                    style={{width: 'auto', cursor: 'pointer'}}
                  />
                  <div>
                    <strong style={{color: 'var(--primary-gold)'}}>⭐ Mettre ce véhicule en vedette</strong>
                    <div style={{fontSize: '0.85rem', color: '#9ca3af', marginTop: '4px'}}>
                      Maximum 3 véhicules en vedette. Actuellement : {stats.featured || 0}/3
                    </div>
                  </div>
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Sauvegarder les modifications
                </button>
                <button 
                  type="button" 
                  onClick={() => setEditingCar(null)}
                  className="btn btn-outline"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des véhicules */}
        <div className="cars-table">
          <h3>Gestion des véhicules</h3>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Photos</th>
                  <th>ID</th>
                  <th>VO</th>
                  <th>Véhicule</th>
                  <th>Année</th>
                  <th>Prix</th>
                  <th>Statut</th>
                  <th>Vedette</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cars.map(car => (
                  <tr key={car.id}>
                    <td className="car-photos-cell">
                      {car.images && car.images.length > 0 ? (
                        <div className="car-photos-preview">
                          <img 
                            src={car.images[0]} 
                            alt={`${car.marque} ${car.modele}`}
                            className="main-photo"
                            onError={(e) => {
                              e.target.src = '/img/defaultImgCar.webp';
                            }}
                          />
                          {car.images.length > 1 && (
                            <div className="photo-count">+{car.images.length - 1}</div>
                          )}
                        </div>
                      ) : (
                        <div className="car-photos-preview">
                          <img 
                            src="/img/defaultImgCar.webp"
                            alt="Image par défaut"
                            className="main-photo"
                          />
                        </div>
                      )}
                    </td>
                    <td>{car.id}</td>
                    <td>{car.vo}</td>
                    <td>
                      <strong>{car.marque} {car.modele}</strong>
                      <br />
                      <small>{car.couleur} - {car.energie}</small>
                    </td>
                    <td>{car.annee}</td>
                    <td>{formatPrice(car.prix)}</td>
                    <td>
                      <span className={`status ${car.disponible ? 'available' : 'sold'}`}>
                        {car.disponible ? 'Disponible' : 'Vendu'}
                      </span>
                    </td>
                    <td style={{textAlign: 'center'}}>
                      {car.enVedette ? (
                        <span style={{
                          fontSize: '1.5rem',
                          display: 'inline-block',
                          animation: 'pulse 2s infinite'
                        }}>⭐</span>
                      ) : (
                        <span style={{color: '#6b7280'}}>-</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          onClick={() => handleEditCar(car)}
                          className="btn btn-small btn-outline"
                        >
                          ✏️ Modifier
                        </button>
                        <button 
                          onClick={() => toggleAvailability(car.id)}
                          className={`btn btn-small ${car.disponible ? 'btn-warning' : 'btn-success'}`}
                        >
                          {car.disponible ? '❌ Marquer vendu' : '✅ Marquer disponible'}
                        </button>
                        <button 
                          onClick={() => handleDeleteCar(car.id)}
                          className="btn btn-small btn-danger"
                        >
                          🗑️ Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 